import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Save, RotateCcw, Eye, EyeOff, Plus, Trash2, GripVertical, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, qs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, Card } from "@/components/Kpi";
import { ComparisonChart, PieBreakdown, useMobile } from "@/components/Charts";

const SPANS = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3", 4: "lg:col-span-4" };
const COLORI = [
  { key: "", label: "Default" },
  { key: "caricato", label: "Rosso · caricato" },
  { key: "liquidato", label: "Verde · liquidato" },
  { key: "sky", label: "Azzurro · premi" },
  { key: "warning", label: "Arancio" },
  { key: "slate", label: "Neutro" },
];
const RING = { caricato: "ring-caricato", liquidato: "ring-liquidato", sky: "ring-sky-500",
  warning: "ring-orange-500", slate: "ring-slate-400" };
/** Applica il titolo personalizzato al blocco: i box usano "label", i grafici "title". */
function conTitolo(node, titolo) {
  if (!titolo || !node?.props) return node;
  const prop = node.props.label !== undefined ? "label"
    : node.props.title !== undefined ? "title" : null;
  return prop ? cloneElement(node, { [prop]: titolo }) : node;
}

const slugSezione = (n) => (n || "generale").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Matitina per rinominare un titolo in modalità modifica. */
function Rinomina({ valore, originale, onSalva, testId }) {
  const [aperto, setAperto] = useState(false);
  const [txt, setTxt] = useState(valore || originale || "");
  if (!aperto) {
    return (
      <button type="button" title="Rinomina" data-testid={testId}
        onClick={() => { setTxt(valore || originale || ""); setAperto(true); }}
        className="text-slate-400 hover:text-sky-700 transition-colors duration-200">
        <Pencil size={13} />
      </button>
    );
  }
  const conferma = () => { onSalva(txt.trim()); setAperto(false); };
  return (
    <span className="inline-flex items-center gap-1">
      <input autoFocus value={txt} data-testid={`${testId}-input`}
        onChange={(e) => setTxt(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") conferma(); if (e.key === "Escape") setAperto(false); }}
        className="text-[11px] border border-sky-400 rounded px-1.5 py-0.5 bg-white w-40 normal-case
          tracking-normal text-slate-800" />
      <button type="button" onClick={conferma} data-testid={`${testId}-ok`}
        className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900">OK</button>
      <button type="button" data-testid={`${testId}-reset`}
        onClick={() => { onSalva(""); setAperto(false); }}
        className="text-[10px] text-slate-400 hover:text-slate-700">originale</button>
    </span>
  );
}

/** Box KPI alimentato da una formula salvata. */
function BloccoFormula({ id, filtri, titolo }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/formule/${id}/valore?${qs(filtri || {})}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [id, JSON.stringify(filtri)]);
  return (
    <KpiCard testId={`blocco-formula-${id}`} label={titolo || d?.nome || "Formula"} value={d?.valore}
      kind={d?.formato === "eur" ? "eurShort" : d?.formato === "perc" ? "perc" : "num"}
      accent="sky" />
  );
}

/** Grafico alimentato da una definizione salvata. */
function BloccoGrafico({ id, filtri, titolo }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/grafici/${id}/dati?${qs(filtri || {})}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [id, JSON.stringify(filtri)]);
  if (!d) return <Card className="p-6 text-sm text-slate-400">Caricamento grafico…</Card>;
  const g = d.grafico;
  if (g.tipo === "pie") {
    return <PieBreakdown testId={`blocco-grafico-${id}`} title={titolo || g.nome} legendSide
      valueKind={g.formato === "eur" ? "eur" : "num"}
      data={(d.dati || []).map((r) => ({ name: r.label, value: r.s0 }))} />;
  }
  return <ComparisonChart testId={`blocco-grafico-${id}`} title={titolo || g.nome} height={320}
    type={g.tipo === "line" ? "line" : "bar"} data={d.dati || []} keys={d.keys || []}
    labelFormat={g.formato === "num" ? "int" : "compact"} />;
}

/** Griglia dashboard con modalità modifica (riordino, visibilità, larghezza, colore, nuovi blocchi). */
export default function GrigliaDashboard({ pagina, blocchi, filtri, className = "" }) {
  const { puo } = useAuth();
  const EDITOR_ABILITATO = puo("modifica_dashboard");
  const [layout, setLayout] = useState(null);
  const [editing, setEditing] = useState(false);
  const modifica = editing && EDITOR_ABILITATO;
  const [dragId, setDragId] = useState(null);
  const [disponibili, setDisponibili] = useState({ formule: [], grafici: [] });
  const [chiuse, setChiuse] = useState({});
  const mobile = useMobile();
  const [initMobile, setInitMobile] = useState(false);

  useEffect(() => {
    api.get(`/layout/${pagina}`).then((r) => setLayout(r.data))
      .catch(() => setLayout({ ordine: [], nascosti: [], span: {}, colore: {}, custom: [],
        titoli: {}, titoli_sezione: {} }));
  }, [pagina]);

  useEffect(() => {
    if (!editing) return;
    Promise.all([api.get("/formule"), api.get("/grafici")])
      .then(([f, g]) => setDisponibili({ formule: f.data.formule || [], grafici: g.data.grafici || [] }))
      .catch(() => setDisponibili({ formule: [], grafici: [] }));
  }, [editing]);

  const tutti = useMemo(() => {
    const custom = (layout?.custom || []).map((c) => ({
      id: c.id, label: c.label, custom: c,
      span: c.tipo === "grafico" ? 4 : 1,
    }));
    // i blocchi con `dopo` (es. dettaglio del giorno) si agganciano subito sotto il blocco indicato
    const ancorati = blocchi.filter((b) => b.dopo);
    const base = [...blocchi.filter((b) => !b.dopo), ...custom];
    const ordine = layout?.ordine || [];
    const pos = (b) => (ordine.indexOf(b.id) === -1 ? 1000 + base.indexOf(b) : ordine.indexOf(b.id));
    const ordinati = [...base].sort((a, b) => pos(a) - pos(b));
    ancorati.forEach((a) => {
      const i = ordinati.findIndex((b) => b.id === a.dopo);
      ordinati.splice(i === -1 ? ordinati.length : i + 1, 0, { ...a, sezione: i === -1 ? a.sezione : ordinati[i].sezione });
    });
    return ordinati;
  }, [blocchi, layout]);

  const sezioni = useMemo(() => {
    const out = [];
    tutti.forEach((b) => {
      const nome = b.sezione || "";
      const ultima = out.find((s) => s.nome === nome);
      if (ultima) ultima.blocchi.push(b);
      else out.push({ nome, blocchi: [b] });
    });
    return out;
  }, [tutti]);

  // su mobile i box KPI e la distribuzione per settore partono chiusi
  useEffect(() => {
    if (!mobile || initMobile || !sezioni.length) return;
    const daChiudere = {};
    sezioni.forEach((s) => {
      if (!s.nome) return;
      if (s.nome === "INDICATORI" || s.nome.includes("AUTO vs NON AUTO")) daChiudere[s.nome] = true;
    });
    setChiuse((c) => ({ ...daChiudere, ...c }));
    setInitMobile(true);
  }, [mobile, initMobile, sezioni]);

  const salva = useCallback(async (next) => {    setLayout(next);
    try {
      await api.put(`/layout/${pagina}`, next);
      toast.success("Layout salvato");
    } catch {
      toast.error("Salvataggio layout non riuscito");
    }
  }, [pagina]);

  if (!layout) return null;
  const nascosti = layout.nascosti || [];

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = tutti.map((b) => b.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setLayout({ ...layout, ordine: ids });
    setDragId(null);
  };

  const aggiungi = (tipo, item) => {
    const id = `${tipo}:${item.id}`;
    if ((layout.custom || []).some((c) => c.id === id)) return toast.error("Blocco già presente");
    setLayout({ ...layout, custom: [...(layout.custom || []),
      { id, tipo, ref: item.id, label: item.nome }] });
  };

  return (
    <div className={className} data-testid={`griglia-${pagina}`}>
      {EDITOR_ABILITATO && (
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button type="button" onClick={() => setEditing((v) => !v)} data-testid="btn-modifica-layout"
          className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md font-semibold border-2
            transition-colors duration-200 ${editing
              ? "bg-sky-600 border-sky-600 text-white hover:bg-sky-700"
              : "bg-white border-sky-500 text-sky-700 hover:bg-sky-50"}`}>
          <Pencil size={15} /> {editing ? "Chiudi modifica" : "Modifica"}
        </button>
        {editing && (
          <>
            <button type="button" onClick={() => salva(layout)} data-testid="btn-salva-layout"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-emerald-600
                text-white font-semibold hover:bg-emerald-700 transition-colors duration-200">
              <Save size={15} /> Salva layout
            </button>
            <button type="button" data-testid="btn-ripristina-layout"
              onClick={async () => {
                await api.delete(`/layout/${pagina}`);
                setLayout({ pagina, ordine: [], nascosti: [], span: {}, colore: {}, custom: [],
                titoli: {}, titoli_sezione: {} });
                toast.success("Layout ripristinato ai valori originali");
              }}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-slate-300
                bg-white text-slate-600 hover:border-slate-500 transition-colors duration-200">
              <RotateCcw size={15} /> Ripristina
            </button>
            <select data-testid="select-aggiungi-blocco" defaultValue=""
              onChange={(e) => {
                const [tipo, id] = e.target.value.split("|");
                const lista = tipo === "formula" ? disponibili.formule : disponibili.grafici;
                const item = lista.find((x) => x.id === id);
                if (item) aggiungi(tipo, item);
                e.target.value = "";
              }}
              className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-white text-slate-700">
              <option value="">+ Aggiungi blocco…</option>
              {disponibili.formule.map((f) => (
                <option key={f.id} value={`formula|${f.id}`}>Box · {f.nome}</option>
              ))}
              {disponibili.grafici.map((g) => (
                <option key={g.id} value={`grafico|${g.id}`}>Grafico · {g.nome}</option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              Trascina per riordinare · usa la matitina per rinominare · {nascosti.length} nascosti
            </span>
          </>
        )}
      </div>
      )}

      {sezioni.map((s) => (
        <div key={s.nome || "_"} data-testid={`sezione-${slugSezione(s.nome)}`} className="mb-6 last:mb-0">
          {s.nome && modifica && (
            <div className="flex items-center gap-2 mb-1 text-[11px] text-slate-500">
              <span>Nome sezione:</span>
              <Rinomina valore={layout.titoli_sezione?.[s.nome]} originale={s.nome}
                testId={`rinomina-sezione-${slugSezione(s.nome)}`}
                onSalva={(v) => setLayout({ ...layout, titoli_sezione: {
                  ...(layout.titoli_sezione || {}), [s.nome]: v } })} />
            </div>
          )}
          {s.nome && (
            <button type="button" data-testid={`sezione-toggle-${slugSezione(s.nome)}`}
              onClick={() => setChiuse((c) => ({ ...c, [s.nome]: !c[s.nome] }))}
              className="w-full flex items-center gap-2 mb-4 px-4 py-2.5 rounded-md border border-slate-200
                bg-white text-left uppercase text-xs tracking-[0.18em] font-semibold text-slate-600
                hover:border-slate-400 hover:text-slate-900 transition-colors duration-200">
              <ChevronDown size={16}
                className={`transition-transform duration-200 ${chiuse[s.nome] ? "-rotate-90" : ""}`} />
              {layout.titoli_sezione?.[s.nome] || s.nome}
              <span className="ml-auto normal-case tracking-normal text-[11px] text-slate-400">
                {chiuse[s.nome] ? "mostra" : "nascondi"}
              </span>
            </button>
          )}
          {!chiuse[s.nome] && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {s.blocchi.map((b) => {
          const nascosto = nascosti.includes(b.id);
          if (nascosto && !modifica) return null;
          if (b.dopo) {
            return <div key={b.id} className={SPANS[b.span] || SPANS[4]} data-testid={`blocco-${b.id}`}>{b.node}</div>;
          }
          const span = layout.span?.[b.id] || b.span || 1;
          const colore = layout.colore?.[b.id] || "";
          return (
            <div key={b.id} className={`${SPANS[span] || SPANS[1]} ${nascosto ? "opacity-40" : ""}
              ${colore ? `ring-2 ring-offset-2 rounded-lg ${RING[colore]}` : ""}`}
              draggable={modifica}
              onDragStart={() => setDragId(b.id)}
              onDragOver={(e) => modifica && e.preventDefault()}
              onDrop={() => onDrop(b.id)}
              data-testid={`blocco-${b.id}`}>
              {modifica && (
                <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-md bg-slate-100/80 border border-slate-200">
                  <GripVertical size={14} className="text-slate-400 cursor-grab" />
                  <span className="text-[11px] font-semibold text-slate-600 truncate max-w-[140px]">
                    {layout.titoli?.[b.id] || b.label || b.id}
                  </span>
                  <Rinomina valore={layout.titoli?.[b.id]} originale={b.label || ""}
                    testId={`rinomina-${b.id}`}
                    onSalva={(v) => setLayout({ ...layout,
                      titoli: { ...(layout.titoli || {}), [b.id]: v } })} />
                  <button type="button" title="Mostra/nascondi" data-testid={`toggle-${b.id}`}
                    onClick={() => setLayout({ ...layout, nascosti: nascosto
                      ? nascosti.filter((x) => x !== b.id) : [...nascosti, b.id] })}
                    className="ml-auto text-slate-500 hover:text-slate-900 transition-colors duration-200">
                    {nascosto ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <select value={span} data-testid={`span-${b.id}`}
                    onChange={(e) => setLayout({ ...layout,
                      span: { ...(layout.span || {}), [b.id]: Number(e.target.value) } })}
                    className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white">
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}/4</option>)}
                  </select>
                  <select value={colore} data-testid={`colore-${b.id}`}
                    onChange={(e) => setLayout({ ...layout,
                      colore: { ...(layout.colore || {}), [b.id]: e.target.value } })}
                    className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white">
                    {COLORI.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  {b.custom && (
                    <button type="button" title="Rimuovi blocco" data-testid={`rimuovi-${b.id}`}
                      onClick={() => setLayout({ ...layout,
                        custom: (layout.custom || []).filter((c) => c.id !== b.id) })}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
              {b.custom
                ? (b.custom.tipo === "grafico"
                  ? <BloccoGrafico id={b.custom.ref} filtri={filtri} titolo={layout.titoli?.[b.id]} />
                  : <BloccoFormula id={b.custom.ref} filtri={filtri} titolo={layout.titoli?.[b.id]} />)
                : conTitolo(b.node, layout.titoli?.[b.id])}
            </div>
          );
        })}
          </div>
          )}
        </div>
      ))}
      {modifica && !tutti.length && (
        <Card className="p-6 text-sm text-slate-500 mt-4">
          <Plus size={14} className="inline mr-1" /> Nessun blocco: aggiungine uno dal menu qui sopra.
        </Card>
      )}
    </div>
  );
}
